import {
  Box,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors-twentyfive-dark";
import React, { FC, useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Document } from "flexsearch";
import { usePrevious } from "@src/utils";
import SearchResult from "./search-result";
import SearchConfig from "../../../static/pit-data/search-config.json";
import SearchIcon from "@mui/icons-material/Search";

const searchIndex = new Document({
  document: SearchConfig,
});

interface SearchProps {
  shuffle: () => void;
}

const Search: FC<SearchProps> = ({ shuffle }) => {
  const [showResuts, setShowResuts] = useState(false);
  const [input, setInput] = useState("");
  const [focused, setFocused] = useState(false);

  const [indexRetrieved, setIndexRetrieved] = useState(false);

  const previousInput = usePrevious(input);

  const [results, setResults] = useState<any[]>([]);

  useEffect(() => {
    const fetchIndex = async () => {
      const response = await fetch(`/pit-data/search-docs-index.json`);
      const result = await response.json();

      for (const indexKey in result) {
        await searchIndex.import(indexKey, result[indexKey]);
      }

      setIndexRetrieved(true);
    };

    fetchIndex().catch(console.error);
  }, []);

  useEffect(() => {
    if (indexRetrieved) {
      if (focused && input.length > 0) {
        setShowResuts(true);

        if (previousInput !== input) {
          const searchResults =
            searchIndex.search({
              query: input,
              enrich: true,
              merge: true,
              limit: 10,
              suggest: true,
            }) ?? [];

          setResults(searchResults);
        }
      } else {
        setShowResuts(false);
      }
    }
  }, [input, focused, indexRetrieved]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
      }}
    >
      <TextField
        placeholder="Explore the taxonomy"
        data-testid="pit-toolbar-search-input"
        value={input}
        onFocus={() => {
          setFocused(true);
        }}
        onBlur={() => {
          setFocused(false);
        }}
        onChange={(e) => {
          setInput(e.target.value);
        }}
        sx={{
          height: "54px",
          width: "100%",
          paddingY: "0px",
          flexGrow: 1,
          borderWidth: "1px",
          "& label": {
            color: `${Colors.PrimaryContrast}14`,
          },
          "& label.Mui-focused": {
            color: `${Colors.PrimaryContrast}50`,
          },
          "& .MuiInput-underline:after": {
            borderBottomColor: "yellow",
          },
          "& .MuiOutlinedInput-root": {
            borderWidth: "1px",
            "& fieldset": {
              borderWidth: "1px",
              borderRadius: "15px",
              borderColor: `${Colors.PrimaryContrast}14`,
            },
            "&:hover fieldset": {
              borderWidth: "1px",
              borderColor: `${Colors.PrimaryContrast}14`,
            },
            "&.Mui-focused fieldset": {
              borderWidth: "1px",
              borderColor: `${Colors.PrimaryContrast}50`,
            },
          },
        }}
        InputProps={{
          sx: {
            height: "54px",
          },
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                sx={{
                  color: Colors.PrimaryContrast,
                }}
              />
            </InputAdornment>
          ),
        }}
      />

      <AnimatePresence>
        {showResuts && (
          <Stack
            data-testid="pit-toolbar-search-results"
            component={motion.div}
            key="search-results"
            initial={{
              top: "100%",
              opacity: 0,
            }}
            animate={{
              top: "calc(100% + 12px)",
              opacity: 1,
            }}
            exit={{
              top: "calc(100% + 24px)",
              opacity: 0,
            }}
            sx={{
              position: "absolute",
              width: {
                xs: "calc(100% + 54px + 54px)",
                md: "100%",
              },
              left: {
                xs: "-54px",
                md: "auto",
              },
              height: "300px",
              overflowY: "auto",
              border: `solid 1px ${Colors.PrimaryContrast}50`,
              borderRadius: "15px",
              backgroundColor: "#00000030",
              backdropFilter: "blur(5px)",
              zIndex: 100,
              top: "calc(100% + 12px)",
              gap: "12px",
              padding: "12px",
            }}
          >
            {results.length > 0 &&
              results.map((result) => {
                return <SearchResult result={result} />;
              })}

            {results.length === 0 && input?.trim() !== "" && (
              <Typography
                variant="body2"
                sx={{
                  position: "absolute",
                  alignSelf: "center",
                  justifyContent: "center",
                  top: "50%",
                  transform: "translate3d(0, -50%, 0)",
                }}
              >
                No results,{" "}
                <Link
                  onClick={() => {
                    shuffle();
                    setInput("");
                  }}
                  sx={{
                    textDecoration: "underline",
                    "&:hover": {
                      textDecorationColor: Colors.PrimaryContrast,
                      cursor: "pointer",
                    },
                  }}
                  data-testid="pit-toolbar-search-no-result-shuffle"
                >
                  try discovering a new category
                </Link>
              </Typography>
            )}
          </Stack>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default Search;
